import { useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Check, FileSpreadsheet, FileUp, Loader2, Sparkles, X } from "lucide-react";
import { api, formatApiError } from "../../../lib/api";

const inputCls = "w-full bg-white border border-navy/10 rounded-lg px-3 py-2.5 text-sm outline-none focus:border-violet transition-colors";

export default function ImportWizard({ onClose }) {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [busy, setBusy] = useState(false);
  const [file, setFile] = useState(null);
  const [meta, setMeta] = useState({ nombre: "", pais: "Argentina", region: "", fecha_inicio: "", fecha_cierre: "", responsable: "", use_cuadrillas: true });
  const [prop, setProp] = useState(null);
  const [rulesText, setRulesText] = useState("");
  const [rulesFile, setRulesFile] = useState(null);
  const [plan, setPlan] = useState(null);
  const [interpreted, setInterpreted] = useState([]);
  const excelRef = useRef(null);
  const docRef = useRef(null);

  const interpret = async () => {
    if (!file) { toast.error("Selecciona el archivo Excel"); return; }
    if (!meta.fecha_inicio || !meta.fecha_cierre) { toast.error("Captura las fechas de inicio y cierre"); return; }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/planning/plans/import-excel", fd);
      setProp(data);
      setStep(2);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "No pude interpretar el Excel"); }
    finally { setBusy(false); }
  };

  const createPlan = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/planning/plans/import-confirm", { proposal_id: prop.proposal_id, ...meta });
      setPlan(data.plan);
      if (data.cuadrillas?.error) toast.info(`Cuadrillas: ${data.cuadrillas.error}`);
      else if (data.cuadrillas) toast.success(`${data.cuadrillas.vinculadas} cuadrilla(s) vinculadas al plan`);
      toast.success(`Plan creado con ${data.unidades} ${prop.config.work_unit_name.toLowerCase()}(s)`);
      if (rulesFile || rulesText.trim()) {
        let out;
        if (rulesFile) {
          const fd = new FormData();
          fd.append("file", rulesFile);
          out = (await api.post(`/planning/plans/${data.plan.id}/rules/from-doc`, fd)).data;
        } else {
          out = (await api.post(`/planning/plans/${data.plan.id}/rules/interpret`, { text: rulesText })).data;
        }
        if (out.interpreted?.length) { setInterpreted(out.interpreted); setStep(3); }
        else { toast.info("No encontré reglas que interpretar"); navigate(`/os/planning/${data.plan.id}`); }
      } else {
        navigate(`/os/planning/${data.plan.id}`);
      }
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Error al crear el plan"); }
    finally { setBusy(false); }
  };

  const confirmRules = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/planning/plans/${plan.id}/rules/confirm`, { rules: interpreted });
      toast.success(`${data.confirmed} regla(s) confirmadas`);
      navigate(`/os/planning/${plan.id}`);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Error al confirmar reglas"); }
    finally { setBusy(false); }
  };

  const cfg = prop?.config;

  return (
    <div className="fixed inset-0 z-[70] bg-navy/50 backdrop-blur-sm flex items-center justify-center p-4">
      <div className="bg-cream rounded-lg w-full max-w-3xl p-6 space-y-4 max-h-[92vh] overflow-y-auto" data-testid="import-wizard">
        <div className="flex items-center justify-between">
          <h3 className="font-medium flex items-center gap-2">
            <FileSpreadsheet className="w-4 h-4 text-emerald-600" /> Importar plan desde Excel
            <span className="text-xs text-navy/40 font-normal">Paso {step} de 3</span>
          </h3>
          <button onClick={onClose} className="text-navy/40 hover:text-navy" data-testid="import-wizard-close"><X className="w-4 h-4" /></button>
        </div>

        {step === 1 && (
          <div className="space-y-4">
            <button type="button" onClick={() => excelRef.current?.click()} data-testid="import-file-btn"
              className={`w-full rounded-xl border-2 border-dashed p-8 text-center transition-colors ${file ? "border-emerald-300 bg-emerald-50/50" : "border-navy/15 bg-white hover:border-violet"}`}>
              <FileSpreadsheet className={`w-8 h-8 mx-auto mb-2 ${file ? "text-emerald-600" : "text-navy/25"}`} />
              {file ? <span className="text-sm font-medium">{file.name}</span>
                : <span className="text-sm text-navy/50">Haz clic para elegir tu Excel (.xlsx o .csv) — las columnas pueden ser cualquiera, la IA lo interpreta</span>}
            </button>
            <input ref={excelRef} type="file" accept=".xlsx,.xlsm,.csv" className="hidden" data-testid="import-file-input"
              onChange={(e) => setFile(e.target.files?.[0] || null)} />
            <div className="grid grid-cols-2 gap-2">
              <input className={inputCls} placeholder="Nombre del plan (opcional, la IA sugiere uno)" value={meta.nombre} data-testid="import-nombre"
                onChange={(e) => setMeta({ ...meta, nombre: e.target.value })} />
              <input className={inputCls} placeholder="Responsable" value={meta.responsable}
                onChange={(e) => setMeta({ ...meta, responsable: e.target.value })} />
              <input className={inputCls} placeholder="País" value={meta.pais} data-testid="import-pais"
                onChange={(e) => setMeta({ ...meta, pais: e.target.value })} />
              <input className={inputCls} placeholder="Región / Provincia / Estado" value={meta.region}
                onChange={(e) => setMeta({ ...meta, region: e.target.value })} />
              <label className="text-[10px] text-navy/40">Fecha de inicio
                <input className={inputCls} type="date" value={meta.fecha_inicio} required data-testid="import-inicio"
                  onChange={(e) => setMeta({ ...meta, fecha_inicio: e.target.value })} /></label>
              <label className="text-[10px] text-navy/40">Fecha objetivo de cierre
                <input className={inputCls} type="date" value={meta.fecha_cierre} required data-testid="import-cierre"
                  onChange={(e) => setMeta({ ...meta, fecha_cierre: e.target.value })} /></label>
            </div>
            <label className="flex items-center gap-2 text-sm text-navy/60" data-testid="import-use-cuadrillas">
              <input type="checkbox" checked={meta.use_cuadrillas} onChange={(e) => setMeta({ ...meta, use_cuadrillas: e.target.checked })} />
              Vincular mis cuadrillas de OMNIA Cuadrillas como recursos del plan (capacidad, efectividad y estado reales)
            </label>
            <button onClick={interpret} disabled={busy || !file} data-testid="import-interpret-btn"
              className="w-full rounded-full bg-gradient-violet text-white py-3 hover:opacity-90 disabled:opacity-50 inline-flex items-center justify-center gap-2">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />} Interpretar Excel con IA
            </button>
          </div>
        )}

        {step === 2 && prop && (
          <div className="space-y-4" data-testid="import-review">
            <div className="rounded-lg border border-violet/25 bg-violet/[0.03] p-4">
              <div className="overline text-navy/40 mb-1">Así interpreté tu Excel</div>
              <p className="text-sm">{prop.resumen}</p>
              {(prop.warnings || []).length > 0 && (
                <div className="mt-2 text-xs text-amber-700">{prop.warnings.map((w, i) => <div key={i}>⚠ {w}</div>)}</div>
              )}
            </div>
            <div className="grid sm:grid-cols-3 gap-2 text-sm">
              <div className="rounded-lg border border-navy/10 bg-white p-3">
                <div className="text-[10px] uppercase tracking-wider text-navy/40">Unidades ({cfg.work_unit_name})</div>
                <div className="font-medium text-lg" data-testid="import-units-count">{prop.units_count}</div>
              </div>
              <div className="rounded-lg border border-navy/10 bg-white p-3">
                <div className="text-[10px] uppercase tracking-wider text-navy/40">Actividades</div>
                <div className="text-xs mt-1 space-y-0.5">{cfg.activities.map((a) => <div key={a.code}>{a.name}{a.audit ? " (auditoría)" : ""}</div>)}</div>
              </div>
              <div className="rounded-lg border border-navy/10 bg-white p-3">
                <div className="text-[10px] uppercase tracking-wider text-navy/40">Recursos</div>
                <div className="text-xs mt-1 space-y-0.5">
                  {cfg.resource_types.length ? cfg.resource_types.map((r) => <div key={r.code}>{r.name} × {r.count}</div>)
                    : <div className="text-navy/40">Sin recursos en el Excel{meta.use_cuadrillas ? " — se vincularán tus cuadrillas" : ""}</div>}
                </div>
              </div>
            </div>
            <div className="rounded-lg border border-navy/10 bg-white overflow-auto max-h-56">
              <table className="w-full text-xs">
                <thead className="bg-navy/[0.03] sticky top-0">
                  <tr>{cfg.variables.map((v) => <th key={v.code} className="px-2 py-1.5 text-left font-medium whitespace-nowrap">{v.name}</th>)}</tr>
                </thead>
                <tbody>
                  {prop.units_preview.map((u, i) => (
                    <tr key={i} className="border-t border-navy/5">
                      {cfg.variables.map((v) => <td key={v.code} className="px-2 py-1 whitespace-nowrap">{String(u[v.code] ?? "")}</td>)}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="rounded-lg border border-navy/10 bg-white p-4 space-y-2">
              <div className="overline text-navy/40">Reglas y políticas (opcional)</div>
              <p className="text-[11px] text-navy/40">Escríbelas aquí o sube un Word/PDF con las reglas, políticas y condiciones que el plan debe respetar. Podrás confirmarlas antes de aplicarlas.</p>
              <textarea className={`${inputCls} min-h-[80px]`} value={rulesText} data-testid="import-rules-text"
                placeholder="Ej.: Los postes se priorizan por mayor cantidad. Reservar un día antes del cierre…"
                onChange={(e) => setRulesText(e.target.value)} />
              <div className="flex items-center gap-2">
                <input ref={docRef} type="file" accept=".docx,.pdf,.txt" className="hidden" data-testid="import-rules-file"
                  onChange={(e) => setRulesFile(e.target.files?.[0] || null)} />
                <button type="button" onClick={() => docRef.current?.click()} data-testid="import-rules-file-btn"
                  className="rounded-full border border-navy/15 text-xs px-4 py-2 hover:border-violet inline-flex items-center gap-1.5">
                  <FileUp className="w-3.5 h-3.5" /> {rulesFile ? rulesFile.name : "Subir Word / PDF con reglas"}
                </button>
                {rulesFile && <button type="button" onClick={() => setRulesFile(null)} className="text-navy/40 hover:text-destructive"><X className="w-3.5 h-3.5" /></button>}
              </div>
            </div>
            <div className="flex gap-2">
              <button onClick={createPlan} disabled={busy} data-testid="import-create-btn"
                className="rounded-full bg-gradient-violet text-white text-sm px-6 py-2.5 hover:opacity-90 disabled:opacity-50 inline-flex items-center gap-2">
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />} Crear plan con esta interpretación
              </button>
              <button onClick={() => setStep(1)} disabled={busy} className="rounded-full border border-navy/15 text-sm px-4 py-2.5 hover:border-navy/40">Volver</button>
            </div>
          </div>
        )}

        {step === 3 && (
          <div className="space-y-3" data-testid="import-rules-review">
            <div className="overline text-navy/50">OMNIA interpretó {interpreted.length} regla(s) de tus políticas</div>
            {interpreted.map((r) => (
              <div key={r.id} className="rounded-lg border border-navy/10 bg-white p-3.5 space-y-1">
                <div className="flex items-center gap-2">
                  <span className="text-[10px] px-2 py-0.5 rounded-full bg-violet/10 text-violet font-medium">{r.type}</span>
                  {r.status === "needs_review" && <span className="text-[10px] px-2 py-0.5 rounded-full bg-amber-50 text-amber-700">Revisar</span>}
                  <button onClick={() => setInterpreted((prev) => prev.filter((x) => x.id !== r.id))}
                    className="ml-auto text-navy/25 hover:text-destructive"><X className="w-4 h-4" /></button>
                </div>
                {r.original && <p className="text-sm italic text-navy/60">"{r.original}"</p>}
                <p className="text-sm">{r.resumen}</p>
              </div>
            ))}
            <div className="flex gap-2">
              <button onClick={confirmRules} disabled={busy || !interpreted.length} data-testid="import-confirm-rules-btn"
                className="rounded-full bg-gradient-violet text-white text-sm px-6 py-2.5 hover:opacity-90 disabled:opacity-50 inline-flex items-center gap-2">
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />} Confirmar reglas e ir al plan
              </button>
              <button onClick={() => navigate(`/os/planning/${plan.id}`)} data-testid="import-skip-rules-btn"
                className="rounded-full border border-navy/15 text-sm px-4 py-2.5 hover:border-navy/40">Omitir e ir al plan</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
